// ============================================================================
// GST SOURCE DOCUMENTS — G-1 slice 3b (ADR-0025 §3)
// ============================================================================
// The files received under one GST engagement: what the client sent, when, and
// what it turned out to be.
//
// Two columns carry the whole point of the design. "Format" is what the bytes
// ARE, sniffed on the server — and when that disagrees with the extension the
// client used, the grid says so, because in the survey a file named .csv was a
// binary workbook and a file named .xls was xlsx. "Status" distinguishes a
// file that is on record but cannot be read (a Word ledger print) from one
// awaiting parse; both were received, and only one can go further.
//
// Upload is receiving only. Parsing into staging, mapping and submission are
// the next steps and have their own screens.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { gstSourceDocumentService } from 'api/services/gstSourceDocumentService';
import { gstExpectedSourceService } from 'api/services/gstExpectedSourceService';
import { gstSubmissionService } from 'api/services/gstSubmissionService';
import {
  GST_DOCUMENT_TYPES,
  GST_SOURCE_DOCUMENT_KINDS,
  type GstExpectedFile,
  type GstReceiveResult,
  type GstDataSubmission,
  type GstEngagement,
  type GstSourceDocument,
} from 'api/types/gstEngagement.types';
import GstCreateSubmissionDialog from './GstCreateSubmissionDialog';
import GstStagingPreviewModal from './GstStagingPreviewModal';

interface Props {
  isOpen: boolean;
  engagement: GstEngagement | null;
  onClose: () => void;
  /** Whether the caller may upload (the `edit` action). Reading the list follows `view`. */
  canUpload?: boolean;
  /** Accepting a client's proposed mapping profile (the `approve` action). */
  canApprove?: boolean;
}

const fyLabel = (y: number) => `FY ${y}-${String((y + 1) % 100).padStart(2, '0')}`;

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const kindLabel = (value: string): string =>
  GST_SOURCE_DOCUMENT_KINDS.find((k) => k.value === value)?.label ?? value;

const streamLabel = (value: string): string =>
  GST_DOCUMENT_TYPES.find((t) => t.value === value)?.label ?? value;

/** "Jul-2026", or "Jul–Sep 2026" for a quarter. Dates arrive as yyyy-MM-dd. */
const feedPeriod = (start: string, end: string): string => {
  const s = new Date(`${start}T00:00:00`);
  const e = new Date(`${end}T00:00:00`);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return `${start}–${end}`;
  const mon = (d: Date) => d.toLocaleDateString('en-GB', { month: 'short' });
  return s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()
    ? `${mon(s)}-${s.getFullYear()}`
    : `${mon(s)}–${mon(e)} ${e.getFullYear()}`;
};

/**
 * What the client's extension CLAIMED the file was, so the grid can flag the
 * cases where the bytes say otherwise. Only the three spreadsheet formats
 * matter here; anything else is simply shown as sniffed.
 */
const claimedFormat = (fileName: string): string | null => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'xlsx': return 'XLSX';
    case 'xls': return 'XLS';
    case 'csv': return 'CSV';
    default: return null;
  }
};

const KIND_OPTIONS = GST_SOURCE_DOCUMENT_KINDS.map((k) => ({
  value: k.value,
  label: `${k.group} · ${k.label}`,
}));

const SOMETHING_ELSE = '__other__';

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * The periods a file can be received for: each month of the year, then each
 * quarter (a QRMP client may send one quarterly file). '' = store only.
 * Values are "start|end".
 */
const periodChoices = (fy: number): { value: string; label: string }[] => {
  const out = [{ value: '', label: 'No period — store only' }];
  for (let i = 0; i < 12; i++) {
    const s = new Date(fy, 3 + i, 1);
    const e = new Date(fy, 4 + i, 0);
    out.push({ value: `${ymd(s)}|${ymd(e)}`, label: s.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) });
  }
  for (let q = 0; q < 4; q++) {
    const s = new Date(fy, 3 + q * 3, 1);
    const e = new Date(fy, 6 + q * 3, 0);
    const m = (d: Date) => d.toLocaleDateString('en-GB', { month: 'short' });
    out.push({ value: `${ymd(s)}|${ymd(e)}`, label: `Quarter ${m(s)}–${m(e)} ${e.getFullYear()}` });
  }
  return out;
};

const periodLabelOf = (value: string): string => {
  const [s, e] = value.split('|');
  const sd = new Date(`${s}T00:00:00`);
  const ed = new Date(`${e}T00:00:00`);
  if (Number.isNaN(sd.getTime()) || Number.isNaN(ed.getTime())) return value;
  return sd.getMonth() === ed.getMonth()
    ? sd.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
    : `${sd.toLocaleDateString('en-GB', { month: 'short' })}–${ed.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}`;
};

const GstSourceDocumentsModal: React.FC<Props> = ({ isOpen, engagement, onClose, canUpload = false, canApprove = false }) => {
  const [docs, setDocs] = useState<GstSourceDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [kindFilter, setKindFilter] = useState('');

  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState('');
  const [notes, setNotes] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);

  // ADR-0025 R1.2: receive AS an expected file, FOR a period.
  const [expectedFiles, setExpectedFiles] = useState<GstExpectedFile[]>([]);
  const [expectedFileId, setExpectedFileId] = useState('');
  const [periodValue, setPeriodValue] = useState('');
  const [lastResult, setLastResult] = useState<GstReceiveResult | null>(null);

  const [submissionFor, setSubmissionFor] = useState<GstSourceDocument | null>(null);
  const [prefill, setPrefill] = useState<React.ComponentProps<typeof GstCreateSubmissionDialog>['prefill']>(null);
  const [preview, setPreview] = useState<GstDataSubmission | null>(null);

  const load = useCallback(async () => {
    if (!engagement) return;
    setLoading(true);
    try {
      const res = await gstSourceDocumentService.getAll(engagement.id, {
        page, pageSize, declaredKind: kindFilter || undefined,
      });
      setDocs(res.data);
      setTotal(res.pagination.totalItems);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not load the documents.');
    } finally {
      setLoading(false);
    }
  }, [engagement, page, pageSize, kindFilter]);

  useEffect(() => {
    if (isOpen) void load();
  }, [isOpen, load]);

  useEffect(() => {
    if (!isOpen) {
      setFile(null); setKind(''); setNotes(''); setPage(1); setKindFilter('');
      setExpectedFileId(''); setPeriodValue(''); setLastResult(null);
    }
  }, [isOpen]);

  // The expected files are what upload offers first; reloaded on every open
  // so a file declared a moment ago is there.
  useEffect(() => {
    if (!isOpen || !engagement || !canUpload) return;
    gstExpectedSourceService.listFiles(engagement.id).then(setExpectedFiles).catch(() => setExpectedFiles([]));
  }, [isOpen, engagement, canUpload]);

  const chosenFile = expectedFiles.find((f) => f.id === expectedFileId) ?? null;

  // Picking an expected file prefills the first month it is still owed for.
  useEffect(() => {
    if (!engagement || !expectedFileId || expectedFileId === SOMETHING_ELSE) return;
    let cancelled = false;
    gstExpectedSourceService.suggestPeriod(engagement.id, expectedFileId)
      .then((p) => { if (!cancelled) setPeriodValue(`${p.taxPeriodStart.slice(0, 10)}|${p.taxPeriodEnd.slice(0, 10)}`); })
      .catch(() => { if (!cancelled) setPeriodValue(''); });
    return () => { cancelled = true; };
  }, [engagement, expectedFileId]);

  const fileOptions = useMemo(() => [
    ...expectedFiles.map((f) => ({
      value: f.id,
      label: `${f.gstin ?? ''} · ${f.sourceRef} · ${f.displayName}${f.learned || f.streams.length === 1 ? '' : ' (layout learned on first file)'}`,
    })),
    { value: SOMETHING_ELSE, label: 'Something else — not an expected file…' },
  ], [expectedFiles]);

  const periodOptions = useMemo(() => (engagement ? periodChoices(engagement.financialYear) : []), [engagement]);

  const isOther = expectedFileId === SOMETHING_ELSE;
  const canReceive = !!file && (isOther ? !!kind : !!expectedFileId);

  const openPreviewFor = useCallback(async (submissionId: string) => {
    if (!engagement) return;
    try { setPreview(await gstSubmissionService.getById(engagement.id, submissionId)); }
    catch { /* the list still shows it */ }
  }, [engagement]);

  const handleUpload = useCallback(async () => {
    if (!engagement || !file || !canReceive) return;
    setConfirmOpen(false);
    setUploading(true);
    try {
      const [start, end] = isOther || !periodValue ? [null, null] : periodValue.split('|');
      const { result, message } = await gstSourceDocumentService.receive(engagement.id, {
        file,
        expectedFileId: isOther ? null : expectedFileId,
        taxPeriodStart: start, taxPeriodEnd: end,
        declaredKind: isOther ? kind : null,
        notes: notes.trim() || null,
      });
      setLastResult(result);

      const doc = result.receipt.document;
      switch (result.outcome) {
        case 'PARSED': {
          const unmapped = result.submissions.some((s) => !s.profileName);
          const errors = result.submissions.reduce((n, s) => n + s.recordsWithErrors, 0)
            + result.messages.filter((m) => m.severity === 'ERROR').length;
          if (errors > 0 || unmapped) notificationService.toast.warning(message, 8000);
          else notificationService.toast.success(message);
          if (result.submissions[0]) void openPreviewFor(result.submissions[0].submissionId);
          break;
        }
        case 'NEEDS_SETUP':
        case 'NEEDS_CONFIRMATION':
          notificationService.toast.info(message, 6000);
          if (result.registrationId && result.sourceRef && result.taxPeriodStart && result.taxPeriodEnd) {
            setPrefill({
              registrationId: result.registrationId, sourceRef: result.sourceRef,
              taxPeriodStart: result.taxPeriodStart, taxPeriodEnd: result.taxPeriodEnd,
              sheetStreams: result.sheetStreams,
            });
          }
          setSubmissionFor(doc);
          break;
        case 'NOT_READABLE':
          notificationService.toast.warning(message, 8000);
          break;
        default:
          notificationService.toast.info(message, 6000);
      }

      setFile(null); setNotes(''); setFileInputKey((k) => k + 1);
      setPage(1);
      await load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not receive the file.');
    } finally {
      setUploading(false);
    }
  }, [engagement, file, canReceive, isOther, periodValue, expectedFileId, kind, notes, load, openPreviewFor]);

  const handleDownload = useCallback(async (doc: GstSourceDocument) => {
    if (!engagement) return;
    try {
      await gstSourceDocumentService.download(engagement.id, doc);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not download the file.');
    }
  }, [engagement]);

  const getRowActions = useCallback((row: GstSourceDocument): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    // Parsing is a write (it creates a submission), so it follows the same
    // grant as upload. A file that cannot be read gets no such action.
    if (canUpload && row.isMachineReadable) {
      // Already parsed: say so in the label. Parsing it again with the same
      // sheets is refused; with different sheets it is a correction.
      items.push({
        id: 'submission',
        label: (row.feeds?.length ?? 0) > 0 ? 'Parse again (different sheets or scope)…' : 'Create submission (parse)…',
        icon: <IconSystem.list size={16} />,
        onClick: () => setSubmissionFor(row),
      });
    }
    items.push({
      id: 'download',
      label: 'Download original',
      icon: <IconSystem.download size={16} />,
      onClick: () => { void handleDownload(row); },
    });
    return items;
  }, [canUpload, handleDownload]);

  const columns: SyutGridColumn<GstSourceDocument>[] = useMemo(() => [
    {
      id: 'receivedAt', header: 'Received', accessorKey: 'receivedAt', size: 140, enableSorting: true,
      cell: ({ row }: any) => formatDateTime(row.original.receivedAt),
    },
    {
      id: 'fileName', header: 'File', accessorKey: 'fileName', enableSorting: true,
      cell: ({ row }: any) => {
        const d = row.original as GstSourceDocument;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontWeight: 500 }}>{d.fileName}</span>
            {d.duplicateOfSourceDocumentId && (
              <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                Same bytes as an earlier receipt — both kept
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'declaredKind', header: 'Declared as', accessorKey: 'declaredKind', size: 170, enableSorting: true,
      cell: ({ row }: any) => kindLabel(row.original.declaredKind),
    },
    {
      id: 'sniffedFormat', header: 'Format', accessorKey: 'sniffedFormat', size: 150, enableSorting: true,
      cell: ({ row }: any) => {
        const d = row.original as GstSourceDocument;
        const claimed = claimedFormat(d.fileName);
        const lied = claimed !== null && claimed !== d.sniffedFormat;
        return (
          <span title={lied ? `The file is ${d.sniffedFormat} but was named .${claimed.toLowerCase()}` : undefined}>
            {d.sniffedFormat}
            {lied && (
              <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#d97706' }}>
                (named .{claimed.toLowerCase()})
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: 'byteSize', header: 'Size', accessorKey: 'byteSize', size: 90, enableSorting: true,
      cell: ({ row }: any) => formatBytes(row.original.byteSize),
    },
    {
      id: 'parseStatus', header: 'Status', accessorKey: 'parseStatus', size: 130, enableSorting: true,
      cell: ({ row }: any) => {
        const d = row.original as GstSourceDocument;
        const cls = d.parseStatus === 'PARSED' ? 'badge badge-active'
          : d.parseStatus === 'UNREADABLE' || d.parseStatus === 'FAILED' ? 'badge badge-inactive'
          : 'badge badge-info';
        const label = d.parseStatus === 'RECEIVED' ? 'Awaiting parse'
          : d.parseStatus === 'UNREADABLE' ? 'Not readable'
          : d.parseStatus === 'FAILED' ? 'Parse failed'
          : 'Parsed';
        return (
          <span
            className={cls}
            style={d.parseStatus === 'UNREADABLE' ? { color: '#d97706' } : undefined}
            title={d.parseError ?? undefined}
          >
            {label}
          </span>
        );
      },
    },
    {
      // What this file already feeds — so nobody parses it again thinking it
      // is new, and a file feeding two branches for one month stands out.
      id: 'feeds', header: 'Parsed into', size: 220, enableSorting: false,
      cell: ({ row }: any) => {
        const feeds = (row.original as GstSourceDocument).feeds ?? [];
        if (feeds.length === 0) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: '0.75rem' }}>
            {feeds.map((f) => (
              <span key={f.submissionId} title={`${f.taxPeriodStart} to ${f.taxPeriodEnd}`}>
                {streamLabel(f.documentType)} · {f.sourceRef} · {feedPeriod(f.taxPeriodStart, f.taxPeriodEnd)} · rev {f.submissionNo}
              </span>
            ))}
          </div>
        );
      },
    },
    { id: 'createdByName', header: 'By', accessorKey: 'createdByName', size: 130, enableSorting: false },
    {
      id: 'actions', header: '', size: 56, enableSorting: false,
      cell: ({ row }: any) => <ActionMenu items={getRowActions(row.original)} />,
    },
  ], [getRowActions]);

  if (!isOpen || !engagement) return null;

  const title = engagement.clientLegalName || engagement.clientName || 'GST engagement';
  const unreadable = docs.filter((d) => !d.isMachineReadable).length;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-xl">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            Source documents — {title} · {fyLabel(engagement.financialYear)}
          </h2>
          <button className="modal-close" onClick={onClose} title="Close">
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          {canUpload && (
            <div
              style={{
                border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
                borderRadius: 6, padding: '0.75rem', marginBottom: 12,
              }}
            >
              <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', marginBottom: 8 }}>
                Receive a file
              </div>
              {expectedFiles.length === 0 && (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 8 }}>
                  No expected files are declared yet. Declare them under <strong>Expected files</strong> on the engagement,
                  and a received file is parsed in the same step.
                </div>
              )}
              <div className="form-grid-2">
                <div className="form-group">
                  <SyutSelect
                    label={<>Which file is it? <span className="text-danger">*</span></>}
                    options={fileOptions}
                    value={expectedFileId}
                    isMulti={false}
                    isClearable={false}
                    size="sm"
                    onChange={(v) => { setExpectedFileId((v as string) || ''); setPeriodValue(''); }}
                    isDisabled={uploading}
                  />
                </div>
                {isOther ? (
                  <div className="form-group">
                    <SyutSelect
                      label={<>What is it? <span className="text-danger">*</span></>}
                      options={KIND_OPTIONS}
                      value={kind}
                      isMulti={false}
                      isClearable={false}
                      size="sm"
                      onChange={(v) => setKind((v as string) || '')}
                      isDisabled={uploading}
                    />
                  </div>
                ) : (
                  <div className="form-group">
                    <SyutSelect
                      label="For the period"
                      options={periodOptions}
                      value={periodValue}
                      isMulti={false}
                      isClearable={false}
                      size="sm"
                      onChange={(v) => setPeriodValue((v as string) || '')}
                      isDisabled={uploading || !expectedFileId}
                    />
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                      {chosenFile ? 'Prefilled with the first month this file is still owed for.' : 'Choose the file first.'}
                    </div>
                  </div>
                )}
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">File <span className="text-danger">*</span></label>
                  <input
                    key={fileInputKey}
                    type="file"
                    className="form-input"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                    disabled={uploading}
                  />
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                    Excel, CSV, or whatever the client sent. The format is read from the file, not its name.
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <input
                    type="text"
                    className="form-input"
                    value={notes}
                    maxLength={1000}
                    placeholder="e.g. received by email from client on 3 Aug"
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={uploading}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                <button
                  className="btn btn-primary"
                  onClick={() => setConfirmOpen(true)}
                  disabled={uploading || !canReceive}
                >
                  {uploading ? 'Receiving…' : !isOther && periodValue ? 'Receive and parse' : 'Receive file'}
                </button>
              </div>

              {lastResult && lastResult.outcome === 'PARSED' && (
                <div style={{ marginTop: 10, borderTop: '1px solid var(--border-color)', paddingTop: 8, fontSize: '0.75rem' }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{lastResult.receipt.document.fileName} — parsed</div>
                  {lastResult.submissions.map((s) => (
                    <div key={s.submissionId} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', color: 'var(--text-secondary)' }}>
                      <span style={{ color: 'var(--text-primary)' }}>{streamLabel(s.documentType)} · rev {s.submissionNo}</span>
                      {s.profileName
                        ? <span>{s.records.toLocaleString()} records via {s.profileName}</span>
                        : <span style={{ color: '#d97706' }}>{s.dataRows.toLocaleString()} rows — no profile fits yet; map it in the preview</span>}
                      {s.recordsWithErrors > 0 && <span style={{ color: '#dc2626' }}>{s.recordsWithErrors.toLocaleString()} with errors</span>}
                      {s.recordsWithWarnings > 0 && <span style={{ color: '#d97706' }}>{s.recordsWithWarnings.toLocaleString()} to confirm</span>}
                      <button type="button" onClick={() => { void openPreviewFor(s.submissionId); }}
                        style={{ border: 'none', background: 'transparent', padding: 0, color: '#2563eb', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.75rem' }}>
                        Open
                      </button>
                    </div>
                  ))}
                  {lastResult.messages.map((m, i) => (
                    <div key={i} style={{ color: m.severity === 'ERROR' ? '#dc2626' : '#d97706' }}>{m.message}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 12 }}>
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              {loading
                ? 'Loading…'
                : `${total} document(s) received.${unreadable > 0 ? ` ${unreadable} on this page cannot be read as a spreadsheet — ask the client for an Excel copy.` : ''}`}
            </div>
            <div style={{ width: 260 }}>
              <SyutSelect
                options={[{ value: '', label: 'All kinds' }, ...KIND_OPTIONS]}
                value={kindFilter}
                isMulti={false}
                isClearable={false}
                size="sm"
                onChange={(v) => { setKindFilter((v as string) || ''); setPage(1); }}
              />
            </div>
          </div>

          {!loading && docs.length === 0 && (
            <div
              style={{
                border: '1px dashed var(--border-color)', borderRadius: 6,
                padding: '1rem', fontSize: '0.8125rem', color: 'var(--text-secondary)',
              }}
            >
              Nothing received yet for this engagement.
            </div>
          )}

          {docs.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <SyutGrid
                data={docs as any}
                columns={columns as any}
                enableSorting
                enablePagination
                manualPagination
                totalCount={total}
                currentPage={page - 1}
                pageSize={pageSize}
                onPageChange={(idx: number) => setPage(idx + 1)}
                onPageSizeChange={(size: number) => { setPageSize(size); setPage(1); }}
                emptyMessage="No documents."
              />
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>

      <GstCreateSubmissionDialog
        isOpen={submissionFor !== null}
        engagement={engagement}
        document={submissionFor}
        prefill={prefill}
        onClose={() => { setSubmissionFor(null); setPrefill(null); }}
        onCreated={(subs) => { setSubmissionFor(null); setPrefill(null); setPreview(subs[0] ?? null); void load(); }}
      />

      <GstStagingPreviewModal
        isOpen={preview !== null}
        engagement={engagement}
        submission={preview}
        canSelectProfile={canUpload}
        canApproveProfile={canApprove}
        onClose={() => setPreview(null)}
      />

      <ConfirmDialog
        open={confirmOpen}
        title="Receive this file?"
        message={
          (isOther
            ? `${file?.name ?? ''} will be recorded as "${kindLabel(kind)}" for ${title}, ${fyLabel(engagement.financialYear)}, and stored as received. `
            : `${file?.name ?? ''} will be recorded as ${chosenFile?.displayName ?? 'the expected file'} (${chosenFile?.sourceRef ?? ''})`
              + (periodValue ? ` for ${periodLabelOf(periodValue)}, and parsed straight away. ` : ', and stored as received. '))
          + 'Files are never replaced — a corrected copy is a new receipt.'
        }
        confirmLabel="Receive"
        cancelLabel="Cancel"
        variant="default"
        loading={uploading}
        loadingMessage="Receiving…"
        onConfirm={() => { void handleUpload(); }}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default GstSourceDocumentsModal;
